// src/oura/visibility/fixtures.ts
//
// The sample businesses that have a Visibility payload of their own. Only
// Nyonya Table does; the other samples show the screen's honest empty state
// rather than numbers generated from a hash of their id.
//
// `?sample=question-change` shows Nyonya Table on the day its questions
// changed, and `?sample=same-day` the same change with the old set asked
// again that morning (nyonya-question-sets.ts), so the change banner, the
// version chips and the split can be previewed without a live business in
// that state. `?sample=three-sets` is that change with one scan per set.

import { fixturePublishedAt, getSearchPerformance, getSearchQuestions } from "@service/aeo/devMock";
import { nyonyaVisibility } from "../fixtures/nyonya-visibility";
import { withQuestionSets } from "../fixtures/nyonya-question-sets";
import type { VisibilityInputs } from "./fromLive";
import { questionKey } from "./questionSets";

// Built once, so the screen's model is not rebuilt on every render. The
// Search Console blocks are devMock's, which the Search performance screen
// also reads for this business, so the two screens agree about every search.
const BASE: VisibilityInputs = {
    ...nyonyaVisibility,
    searchQuestions: getSearchQuestions("nyonya-table"),
    searchPerformance: getSearchPerformance("nyonya-table"),
};
const NYONYA = withQuestionSets(BASE, "default");
const NYONYA_CHANGED = withQuestionSets(BASE, "question-change");
const NYONYA_SAME_DAY = withQuestionSets(BASE, "same-day");
const NYONYA_THREE_SETS = withQuestionSets(BASE, "three-sets");

function sampleStory(): string | null {
    try {
        return typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("sample");
    } catch {
        return null;
    }
}

export function visibilityFixture(businessId: string): VisibilityInputs | null {
    if (businessId !== "nyonya-table") return null;
    const story = sampleStory();
    return story === "question-change" ? NYONYA_CHANGED : story === "three-sets" ? NYONYA_THREE_SETS : story === "same-day" ? NYONYA_SAME_DAY : NYONYA;
}

/**
 * The sample's scans as published or held on this preview: every finished
 * sample scan starts published, and Publish / Hide in Question history saves
 * the change in this browser, so a client tab sees what a staff tab set.
 */
export function withFixturePublished(inputs: VisibilityInputs): VisibilityInputs {
    return {
        ...inputs,
        history: inputs.history.map((h) =>
            fixturePublishedAt(h.published_at === undefined && (h.status ?? "complete") === "complete" ? { ...h, published_at: h.captured_at } : h),
        ),
    };
}

/**
 * The sample as it stood on one of its earlier scans, for a version picked on
 * Named over time: that scan's questions and verdicts in place of today's, so
 * the table follows the pick the way a live business's page does when it opens
 * that scan. The earlier sets are edits of today's 50 in the same slots, so
 * each answer is today's, under the old question's words, named or not as that
 * scan recorded. Unchanged when the sample has no questions for that scan.
 */
export function fixtureAtScan(inputs: VisibilityInputs, scanId: string | null): VisibilityInputs {
    const scan = scanId ? inputs.questionSets?.[scanId] : undefined;
    const entry = scanId ? inputs.history.find((h) => h.assessment_id === scanId) : undefined;
    if (!scan || !entry) return inputs;
    const rename = new Map(inputs.queries.map((q, i) => [q.text, scan.queries[i]?.text ?? q.text]));
    const named = (text: string) => scan.named?.[questionKey(text)] ?? false;
    const probes = inputs.probes?.map((p) => {
        const query = rename.get(p.query) ?? p.query;
        return { ...p, query, mentioned: p.mentioned && named(query) };
    });
    // Named on that scan but by no engine today: its first answer names it.
    const mentioned = new Set((probes ?? []).filter((p) => p.mentioned).map((p) => p.query));
    const fixed = probes?.map((p) => {
        if (mentioned.has(p.query) || !named(p.query)) return p;
        mentioned.add(p.query);
        return { ...p, mentioned: true };
    });
    return {
        ...inputs,
        assessmentId: scan.assessmentId,
        capturedAt: scan.capturedAt,
        queries: scan.queries,
        probes: fixed,
        googleAiReadings: inputs.googleAiReadings?.map((r) => {
            const query = rename.get(r.query) ?? r.query;
            return { ...r, query, mentioned: r.shown === true ? !!r.mentioned && named(query) : r.mentioned };
        }),
        vis: inputs.vis && entry.visibility
            ? { ...inputs.vis, questionsNamed: entry.visibility.questionsNamed ?? undefined, capturedAt: scan.capturedAt }
            : inputs.vis,
        trendBreak: null,
        prior: null,
    };
}
